import { useTranslation } from "react-i18next";
import { Controller, Control, FieldValues, Path } from "react-hook-form";
import "./colorField.scss";

export interface ColorFieldProps<TFieldValues extends FieldValues> {
  /** The RHF field name — also seeds every id this component generates, the same convention
   * every sibling in this layer uses. The stored value is the plain hex string ("#3B82F6"), the
   * same shape `TagsManagement`'s own `tagColor` state holds today. */
  name: Path<TFieldValues>;
  /** The field's overall visible label (already translated by the caller). */
  label: string;
  control: Control<TFieldValues>;
  /** The palette of preset hex colors rendered as swatch buttons, in order. Pass
   * `TagsManagement`'s own ten-color list to preserve the current screen's palette. */
  presets: string[];
  /** The accessible name of the custom-color `<input type="color">` — this is the one thing the
   * current hybrid picker gets wrong (an unlabeled native color input); this component fixes it
   * by requiring a real label here, same as `PhoneField`'s `countryCodeLabel`. */
  customColorLabel: string;
  /** Appends the translated `form.optional` marker ("(optional)") to `label`. Same convention
   * and rationale as `TextField`'s `optional` — see that component's docstring. */
  optional?: boolean;
  disabled?: boolean;
}

/**
 * The hex-string color picker `TagsManagement.tsx` currently hand-rolls (line ~895 onward): ~10
 * preset swatch buttons plus a native `<input type="color">` for anything outside the palette,
 * bound to a single React Hook Form value via `Controller` — same guarantees as every sibling in
 * this layer.
 *
 * ## What the current screen gets right, and preserves here
 *
 * Every preset swatch is already a real `<button>` with `aria-label={`Select color ${color}`}` —
 * accessible today, and kept verbatim (both the element and the label pattern) so
 * `getByRole("button", { name: "Select color #EF4444" })` keeps resolving exactly like it does
 * against the current screen.
 *
 * ## What it gets wrong, and this component fixes
 *
 * The native `<input type="color">` (line ~914) carries no label at all — reachable only by
 * clicking the swatch, invisible to `getByLabelText`/a screen reader. `customColorLabel` makes
 * that a real `<label htmlFor>` pairing, exactly like `PhoneField`'s `countryCodeLabel` for its
 * otherwise-unlabeled dial-code select.
 *
 * ## Why this isn't `Omit<ComponentPropsWithoutRef<typeof SomeCarbonInput>, ...>` like its
 * siblings
 *
 * `TextField`/`SelectField`/`PhoneField`/`AutocompleteField` all wrap exactly one (or, for
 * `PhoneField`, two) Carbon form control(s). A color picker built from a row of plain `<button>`
 * swatches plus a native `<input type="color">` has no single Carbon component to delegate
 * pass-through props to — Carbon ships no color-picker primitive — so this component owns its
 * own minimal prop surface instead of an `Omit<>` of someone else's.
 *
 * ## Controlled, no drift
 *
 * The selected swatch (`aria-pressed`) and the custom input's own value are both derived from
 * `field.value` on every render — clicking a swatch or picking a custom color both go through
 * `field.onChange`, never local state, the same discipline `PhoneField`'s composite value keeps.
 *
 * ## Error and optional guarantees
 *
 * `fieldState.error`, when present, renders as a `role="alert"` message with a stable id that
 * both the group and the custom color input's `aria-errormessage` point at — the same
 * announce-the-real-error guarantee `TextField` makes, adapted to a composite control that has
 * no single input to hang Carbon's own `invalid`/`invalidText` off of. `optional` appends the
 * same translated `form.optional` marker every sibling in this layer uses.
 */
function ColorField<TFieldValues extends FieldValues>({
  name,
  label,
  control,
  presets,
  customColorLabel,
  optional,
  disabled,
}: ColorFieldProps<TFieldValues>) {
  const { t } = useTranslation();
  const labelText = optional ? `${label} ${t("form.optional")}` : label;
  const groupLabelId = `${name}-label`;
  const customInputId = `${name}-custom`;
  const errorId = `${name}-error-msg`;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const value = (field.value as string | undefined) ?? "";
        const hasError = Boolean(fieldState.error);

        return (
          <div className="color-field">
            {/* Not a <label htmlFor>: this text fronts a button group, not a single input — it's
                tied to that group via aria-labelledby below instead. */}
            <div className="color-field__label" id={groupLabelId}>
              {labelText}
            </div>
            <div
              className="color-field__presets"
              role="group"
              aria-labelledby={groupLabelId}
            >
              {presets.map((color) => {
                const isSelected = value.toLowerCase() === color.toLowerCase();
                return (
                  <button
                    key={color}
                    type="button"
                    className="color-field__swatch"
                    style={{ backgroundColor: color }}
                    aria-label={`Select color ${color}`}
                    aria-pressed={isSelected}
                    disabled={disabled}
                    onClick={() => field.onChange(color)}
                    onBlur={field.onBlur}
                  />
                );
              })}
            </div>
            <div className="color-field__custom">
              <label htmlFor={customInputId}>{customColorLabel}</label>
              <input
                id={customInputId}
                type="color"
                className="color-field__custom-input"
                value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : "#000000"}
                disabled={disabled}
                onChange={(event) => field.onChange(event.target.value)}
                onBlur={field.onBlur}
                aria-invalid={hasError}
                aria-errormessage={hasError ? errorId : undefined}
              />
              <span className="color-field__custom-value">{value}</span>
            </div>
            {hasError ? (
              <div id={errorId} role="alert" className="color-field__error">
                {fieldState.error?.message}
              </div>
            ) : null}
          </div>
        );
      }}
    />
  );
}

export default ColorField;
